import { Button, Icon } from 'copilot-design-system';
import { useTranslation } from 'react-i18next';

import history from 'src/history';
import { useAppSelector } from 'src/hooks/useStore';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { ChartData } from 'src/types/analytics';

import { Chart } from './Chart';

const MAX_CLIENTS = 100;

function getRandomInt(minBoundary: number, maxBoundary: number): number {
  const minInt = Math.ceil(minBoundary);
  const maxInt = Math.floor(maxBoundary);
  return Math.floor(Math.random() * (maxInt - minInt + 1)) + minInt;
}

const today = new Date();

// To display the empty state, we generate a set of randomized
// data points for the last 30 days. Here's where that generation
// is done. A lot of this is just fine tuning to create the UI we
// want, that's the only requirement of this data is to look
// reasonable as a curve.
//
// Honestly, this whole thing could be replaced with a static data
// set or even an SVG. But this seemed fun.
const data: ChartData[] = new Array(30)
  .fill(null)
  .reduce<ChartData[]>((acc, _, i) => {
    // Keep the previous total in memory, we'll use that for the current day total.
    const prevTotal = acc[acc.length - 1]?.total ?? 0;

    // We have to generate a number of added clients.
    let added = 0;

    // This check makes sure we start at 0, it's intended to skiip
    // this block on the first loop.
    if (i > 0) {
      if (i === 1) {
        // The random number we generate can be 0 and that creates
        // the possibility of having no data points on the chart.
        // A simple hack is to make sure clients are added on day 2.
        added = 3;
      } else if (i === 29) {
        // On the last day we want to make sure we reach 100 clients.
        added = 100 - prevTotal;
      } else {
        // Otherwise, let's just generate a random number of clients,
        // but make sure we don't go over the max.
        added = getRandomInt(2, Math.min(12, MAX_CLIENTS - prevTotal));
      }
    }

    // Removed is quite a bit simpler, we just want to make sure we
    // don't go below 0 clients. Added tends to be higher than removed
    // because we want 📈
    const removed = getRandomInt(0, Math.min(6, prevTotal - 1));

    // Generate a date stamp for the day.
    const date = new Date(today);
    date.setDate(today.getDate() - (30 - i));

    acc.push({
      date: date,
      total: prevTotal + added - removed,
    });

    return acc;
  }, []);

export function EmptyState() {
  const clientUsers = useAppSelector((state) => state.clients.clients);
  const { t } = useTranslation();

  return (
    <>
      <div className="pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex sm:items-center xs:items-start">
          <Icon
            icon="Info"
            className="w-3.5 h-3.5 pr-1.5 mt-0.5 sm:mt-0 text-gray-500"
          />
          <BaseTypography fontType="14Regular" className="flex-1 text-gray-500">
            {[
              'Metrics will show after you create 10',
              t(($) => $.client, { count: 2 }),
              `(${clientUsers.length}/10 created so far)`,
            ].join(' ')}
          </BaseTypography>
        </div>
        <div
          id={'analytics-go-to-crm'}
          className="[&>button]:cursor-pointer pt-4 sm:pt-0"
        >
          <Button
            onClick={() => history.push('/clients/users')}
            label="Go to CRM"
            variant="secondary"
          />
        </div>
      </div>
      <div className="text-gray-300">
        <Chart data={data} />
      </div>
    </>
  );
}
